"use client"
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Loader2 } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import * as z from "zod";

const feedbackSchema = z.object({
  feedback: z.string().min(1, "Feedback cannot be empty")
});

interface RestaurantFeedbackProps {
  isOpen: boolean;
  onClose: () => void;
}

export function RestaurantFeedback({ isOpen, onClose }: RestaurantFeedbackProps) {
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmitFeedback = async () => {
    try {
      feedbackSchema.parse({ feedback });
      
      setIsSubmitting(true);
      const response = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          feedback: feedback,
          feedbackType: 'restaurant'
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      toast({
        title: "Success!",
        description: "Thank you for your feedback!",
        duration: 1500,
      });
      
      setFeedback('');
      onClose();

    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          variant: "destructive", 
          title: "Error",
          description: "We would love to hear from you, but please enter some feedback before submitting.",
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to submit feedback. Please try again.",
        });
        console.error('Error submitting feedback:', error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Interested in contributing?</AlertDialogTitle>
          <AlertDialogDescription>
            We are seeking to expand our inventory of restaurants. If you are interested in contributing, please get in touch.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="Let us know how you can help us!"
          className="mt-2 w-full min-h-[100px]"
          disabled={isSubmitting}
        />
        <AlertDialogFooter>
          <Button onClick={onClose} variant="outline" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button onClick={handleSubmitFeedback} disabled={isSubmitting}>
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Submitting...
              </>
            ) : (
              'Submit Feedback'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
